// src/hooks/canvas/useTypeMenus.js
// Secili nesneye gore uygun TypeMenu'yu goster/gizle.
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import * as THREE from 'three';

const MENU_KEYS = [
  'socket', 'lightSwitch', 'lightFixture', 'panel',
  'mainPanel', 'box', 'cabin', 'machine', 'bell', 'cableShaft',
];
const INIT = { visible: false, position: { x: 0, y: 0 }, id: null, details: {}, moved: false, userData: null };

function createInitialMenuState() {
  const state = {};
  for (const key of MENU_KEYS) state[key] = { ...INIT };
  return state;
}

function getScreenPosition(obj, camera, renderer) {
  const worldPos = new THREE.Vector3();
  obj.getWorldPosition(worldPos);
  const vec = worldPos.project(camera);
  return {
    x: ((vec.x + 1) / 2) * renderer.domElement.clientWidth,
    y: ((-vec.y + 1) / 2) * renderer.domElement.clientHeight,
  };
}

// Scene traverse ile secili nesneleri tip bazinda topla
function collectSelectedObjects(scene, selectedObjectIds) {
  const result = { hasWireTitle: false };
  for (const key of MENU_KEYS) result[key] = null;

  scene.traverse(obj => {
    if (!obj.userData?.id) return;
    if (!selectedObjectIds.includes(obj.userData.id)) return;

    const type = obj.userData.type;
    const func = obj.userData.function;

    if (type === 'wireTitle') result.hasWireTitle = true;
    if (type === 'supplyPoint') result.box = obj;
    else if (type === 'cabin') result.cabin = obj;
    else if (type === 'mainPanel') result.mainPanel = obj;
    else if (type === 'electricalPanel') result.panel = obj;
    else if (type === 'socket') result.socket = obj;
    else if (type === 'LightSwitch') result.lightSwitch = obj;
    else if (type === 'LightFixture') result.lightFixture = obj;
    else if (type === 'machine') result.machine = obj;
    else if (type === 'bellTrafo' || type === 'BellTrafo' || type === 'bell' ||
             type === 'bellButton' || type === 'wireBell' || func === 'wireBell') {
      result.bell = obj;
    }
    else if (type === 'cableShaft') {
      let shaftObj = obj;
      if ((obj.userData.isFill || obj.userData.isBorder) && obj.parent?.userData?.type === 'cableShaft') {
        shaftObj = obj.parent;
      }
      if (!result.cableShaft || result.cableShaft.userData?.isFill || result.cableShaft.userData?.isBorder) {
        result.cableShaft = shaftObj;
      }
    }
  });
  return result;
}

// Secili nesnelerden menu state'lerini hesapla
function computeMenuUpdates(selected, camera, renderer) {
  const updates = {};
  for (const key of MENU_KEYS) updates[key] = { visible: false };

  // wireTitle seciliyse panel menusunu gosterme
  const effectivePanel = selected.hasWireTitle ? null : selected.panel;

  const simple = [
    ['box', selected.box],
    ['cabin', selected.cabin],
    ['mainPanel', selected.mainPanel],
    ['panel', effectivePanel],
    ['socket', selected.socket],
    ['lightSwitch', selected.lightSwitch],
    ['lightFixture', selected.lightFixture],
    ['machine', selected.machine],
    ['bell', selected.bell],
  ];

  for (const [key, obj] of simple) {
    if (obj) {
      const pos = getScreenPosition(obj, camera, renderer);
      updates[key] = { visible: true, position: pos, id: obj.userData.id, details: obj.userData, userData: obj.userData };
    }
  }

  // cableShaft ozel pozisyon hesaplama
  if (selected.cableShaft) {
    const shaftCenter = selected.cableShaft.userData?.center;
    let pos;
    if (shaftCenter) {
      const worldPos = new THREE.Vector3(shaftCenter.x, shaftCenter.y, shaftCenter.z || 0);
      const vec = worldPos.project(camera);
      pos = {
        x: ((vec.x + 1) / 2) * renderer.domElement.clientWidth,
        y: ((-vec.y + 1) / 2) * renderer.domElement.clientHeight,
      };
    } else {
      pos = getScreenPosition(selected.cableShaft, camera, renderer);
    }
    updates.cableShaft = { visible: true, position: pos, id: selected.cableShaft.userData.id, details: selected.cableShaft.userData, userData: selected.cableShaft.userData };
  }

  // lightSwitch subtype normalizasyonu
  if (updates.lightSwitch.visible && selected.lightSwitch) {
    let subtype = selected.lightSwitch.userData.subtype || 'normal';
    if (subtype === 'komutator') subtype = 'Komutatir';
    if (subtype === 'etanj') subtype = 'normal etanj';
    updates.lightSwitch.subtype = subtype;
  }

  // cabin ozel details
  if (updates.cabin.visible && selected.cabin) {
    updates.cabin.details = {
      'ID': selected.cabin.userData.id,
      'Tip': selected.cabin.userData.subtype || '-',
      'basePoint': selected.cabin.userData.basePoint,
      'nodeId': selected.cabin.userData.nodeId,
      'Konum': `(${selected.cabin.position.x.toFixed(1)}, ${selected.cabin.position.y.toFixed(1)})`,
    };
  }

  // machine ozel details
  if (updates.machine.visible && selected.machine) {
    updates.machine.details = {
      'ID': selected.machine.userData.id,
      'Tip': selected.machine.userData.subtype || '-',
      'Konum': `(${selected.machine.position.x.toFixed(1)}, ${selected.machine.position.y.toFixed(1)})`,
    };
  }

  // bell ozel details
  if (updates.bell.visible && selected.bell) {
    updates.bell.details = {
      'ID': selected.bell.userData.id,
      'type': selected.bell.userData.type || selected.bell.userData.function || '-',
      'branchID': selected.bell.userData.branchID || '-',
    };
  }

  return updates;
}

export default function useTypeMenus(scene, camera, renderer) {
  const selectedObjectIds = useSelector((state) => state.selection.selectedObjectIds);
  const [menus, setMenus] = useState(createInitialMenuState);

  const handleDrag = useCallback((menuKey, dx, dy) => {
    setMenus(prev => ({
      ...prev,
      [menuKey]: {
        ...prev[menuKey],
        position: { x: prev[menuKey].position.x + dx, y: prev[menuKey].position.y + dy },
        moved: true,
      },
    }));
  }, []);

  const handleClose = useCallback((menuKey) => {
    setMenus(prev => ({
      ...prev,
      [menuKey]: { ...prev[menuKey], visible: false, moved: false },
    }));
  }, []);

  useEffect(() => {
    if (!scene || !camera || !renderer) return;
    const selected = collectSelectedObjects(scene, selectedObjectIds);
    const updates = computeMenuUpdates(selected, camera, renderer);

    setMenus(prev => {
      const next = { ...prev };
      for (const key of MENU_KEYS) {
        if (updates[key].visible) {
          next[key] = {
            ...prev[key],
            visible: true,
            position: prev[key].moved ? prev[key].position : updates[key].position,
            id: updates[key].id,
            details: updates[key].details || prev[key].details,
            userData: updates[key].userData || prev[key].userData,
            subtype: updates[key].subtype,
          };
        } else {
          next[key] = { ...prev[key], visible: false };
        }
      }
      return next;
    });
  }, [scene, selectedObjectIds]);

  return { menus, handleDrag, handleClose, selectedObjectIds };
}
